// Unified Storage Adapter for Desktop (Electron) and Mobile (Capacitor)
// Provides consistent API regardless of platform
import { Capacitor } from '@capacitor/core';

/**
 * Threshold (in bytes) above which a Capacitor SQLite plugin call is
 * flagged in the console. Capacitor serializes call arguments across
 * the native bridge as JSON; a single call over 5 MB routinely causes
 * OutOfMemoryError on low-end Android devices when the bridge tries to
 * build a String from the full payload.
 *
 * Logging is best-effort: serialization failures (e.g. circular refs)
 * are swallowed so the actual plugin call still proceeds.
 */
const SAFE_CALL_SIZE_THRESHOLD = 5 * 1024 * 1024;

/**
 * Safe wrapper for SQLite connection method calls.
 * The existing behavior (forward the call, return its promise) is unchanged;
 * we only add size warning so regressions in bulk-write paths are visible.
 *
 * @param {object} connection SQLiteConnection instance
 * @param {string} method Method name on the connection
 * @param {Array} args Arguments being forwarded to the method
 * @param {Object} [opts] Optional context label for warnings
 * @returns {Promise<any>} The method call's resolved value
 */
function safeCall(connection, method, args = [], opts = {}) {
    const { context } = opts || {};
    try {
        const serialized = JSON.stringify(args);
        if (serialized && serialized.length > SAFE_CALL_SIZE_THRESHOLD) {
            const where = context ? ` (${context})` : '';
            console.warn(`[storageAdapter] Oversized ${connection?.constructor?.name || 'connection'}.${method} call${where}: ${(serialized.length / 1024 / 1024).toFixed(2)} MB`);
        }
    } catch (e) { /* ignore */ }
    return connection[method](...args);
}

/**
 * Storage Adapter Class
 * Automatically detects platform and uses appropriate storage backend
 */
class StorageAdapter {
    constructor() {
        this.platform = this.detectPlatform();
        this.db = null;
        this.initPromise = null; // Prevent concurrent initialization races
        this.dbConnection = null; // For Capacitor SQLite connection
        // Live RW name is always sandbox. Lytsoft durability is file backup only.
        this.dbName = 'pos-offline';
    }

    /**
     * Detect the current platform
     * @returns {string} 'electron' | 'mobile' | 'indexeddb'
     */
    detectPlatform() {
        // 1. Electron - Trusted UserAgent check
        if (typeof navigator !== 'undefined' && navigator.userAgent.toLowerCase().includes(' electron/')) {
            return 'electron';
        }

        // 2. Mobile (Native) - Trusted Capacitor check
        if (typeof Capacitor !== 'undefined' && Capacitor.isNativePlatform()) {
            console.log('[StorageAdapter] Environment: Native Mobile (via Capacitor.isNativePlatform)');
            return 'mobile';
        }

        // 3. Fallback - Web/Dev
        console.log('[StorageAdapter] Environment: Web/Unknown. Fallback to IndexedDB.');
        return 'indexeddb';
    }

    /**
     * Initialize the mobile SQLite database via `@capacitor-community/sqlite`.
     *
     * **Encryption policy: NONE (deliberate).** The native plugin's global
     * `isEncryption` flag is forced off via
     * `capacitor.config.json → plugins.CapacitorSQLite.androidIsEncryption = false`
     * AND this method opens the per-connection database with
     * `encrypted = false` and `mode = 'no-encryption'`.
     *
     * Both flags must stay aligned. The plugin-level flag prevents the
     * plugin from touching the Android Keystore at load time (which crashes
     * on devices with restricted or broken KeyStores — Xiaomi, Huawei,
     * rugged handhelds — with "keystore failed"). The per-connection
     * `false` prevents per-connection SQLCipher at runtime.
     *
     * If the Android-side `sqliteDatabase.java` encrypted-DB APIs are
     * re-introduced later, treat this comment as the contract that must be
     * jointly flipped.
     *
     * @returns {Promise<void>}
     */
    async initMobileSQLite() {
        try {
            // Check if we're actually on a native platform
            if (!Capacitor.isNativePlatform()) {
                throw new Error('Not running on a native platform. SQLite is only available on Android/iOS.');
            }

            console.log('[StorageAdapter] Platform:', Capacitor.getPlatform());

            // Wait for Capacitor to be fully ready before accessing plugins
            // This is critical - plugins may not be available immediately on app start
            console.log('[StorageAdapter] Waiting for Capacitor platform ready...');
            await new Promise((resolve) => {
                if (document.readyState === 'complete') {
                    resolve();
                } else {
                    window.addEventListener('load', resolve, { once: true });
                }
            });

            // Additional small delay to ensure native bridge is fully initialized
            await new Promise(resolve => setTimeout(resolve, 100));
            console.log('[StorageAdapter] Platform ready');

            // Android: live RW DB is always sandbox `pos-offline`. Absolute paths
            // under public Lytsoft/ are NCDB/read-only in capacitor-community/sqlite
            // ("database ... not opened" on execute). Durability = restore/backup copies.
            if (Capacitor.getPlatform() === 'android') {
                try {
                    const { prepareAndroidOfflineDb } = await import('./lytsoftDataDir');
                    const prep = await prepareAndroidOfflineDb();
                    this.dbName = prep.dbConnectionName || this.dbName;
                    console.log(
                        '[StorageAdapter] Android offline dbName=',
                        this.dbName,
                        prep.durable ? '(sandbox+Lytsoft backup)' : '(sandbox only)',
                        prep.restored ? 'restored-from-durable' : ''
                    );
                } catch (e) {
                    console.warn(
                        '[StorageAdapter] Lytsoft prepare failed, using sandbox:',
                        e && e.message
                    );
                    this.dbName = 'pos-offline';
                }
            }

            // Dynamic import to avoid issues when plugin is not installed
            const sqliteModule = await import('@capacitor-community/sqlite');
            console.log('[StorageAdapter] SQLite module imported');
            console.log('[StorageAdapter] Module exports:', Object.keys(sqliteModule));

            let { CapacitorSQLite, SQLiteConnection } = sqliteModule;

            // Check if CapacitorSQLite plugin is available
            console.log('[StorageAdapter] CapacitorSQLite type:', typeof CapacitorSQLite);
            console.log('[StorageAdapter] CapacitorSQLite value:', CapacitorSQLite);

            // Try alternative access method via Capacitor.Plugins if module import fails
            if (!CapacitorSQLite && window.Capacitor?.Plugins?.CapacitorSQLite) {
                console.log('[StorageAdapter] Trying fallback: accessing via window.Capacitor.Plugins');
                CapacitorSQLite = window.Capacitor.Plugins.CapacitorSQLite;
                console.log('[StorageAdapter] Fallback CapacitorSQLite:', CapacitorSQLite);
            }

            if (!CapacitorSQLite) {
                console.error('[StorageAdapter] CapacitorSQLite is null/undefined');
                console.error('[StorageAdapter] Module keys:', Object.keys(sqliteModule));
                console.error('[StorageAdapter] Available on window.Capacitor.Plugins:',
                    window.Capacitor?.Plugins ? Object.keys(window.Capacitor.Plugins) : 'window.Capacitor.Plugins not available');
                throw new Error('CapacitorSQLite plugin is not available. The native plugin may not be properly registered.');
            }

            if (!SQLiteConnection) {
                console.error('[StorageAdapter] SQLiteConnection is null/undefined');
                throw new Error('SQLiteConnection class is not available from @capacitor-community/sqlite');
            }

            console.log('[StorageAdapter] CapacitorSQLite available methods:', Object.keys(CapacitorSQLite));
            console.log('[StorageAdapter] Initializing SQLiteConnection...');
            const sqlite = new SQLiteConnection(CapacitorSQLite);

            // After a WebView reload the JS-side and native-side connection
            // maps in @capacitor-community/sqlite go out of sync — the
            // native plugin still owns the connection but the JS bridge
            // forgot about it, so createConnection throws "already exists"
            // and retrieveConnection throws "does not exist".
            // checkConnectionsConsistency forces both maps back in sync.
            try {
                if (typeof sqlite.checkConnectionsConsistency === 'function') {
                    await sqlite.checkConnectionsConsistency();
                }
            } catch (e) {
                console.warn('[StorageAdapter] checkConnectionsConsistency warned:', e && e.message);
            }

            // Best-effort: close any stale connection so the create path
            // below starts from a known-clean state. Tolerate "does not
            // exist" — that just means there was nothing to clean up.
            try {
                await sqlite.closeConnection(this.dbName, false);
            } catch (e) {
                const m = (e && e.message) || '';
                if (!/does not exist/i.test(m)) {
                    console.warn('[StorageAdapter] closeConnection warned:', m);
                }
            }

            // Decide whether to retrieve or create. After a WebView reload
            // the JS side forgets the connection but the native plugin
            // still owns it, so isConnection() can lie. We try the optimal
            // path first and fall back if either side disagrees.
            const isConn = (await sqlite.isConnection(this.dbName, false)).result;

            /**
             * Create a new Capacitor SQLite connection.
             *
             * We pass `encrypted = false` and `mode = 'no-encryption'` so the
             * per-connection database file is opened as a plain (unencrypted)
             * SQLite file. This matches `capacitor.config.json`, where the
             * `plugins.CapacitorSQLite.androidIsEncryption` flag is set to
             * false. Without that plugin-level flag the native plugin
             * defaults to encryption ON, which forces it to materialize an
             * AES master key from the Android Keystore at plugin load —
             * a path that crashes on devices where the Android KeyStore is
             * unavailable or restricted (Xiaomi, Huawei, rugged handhelds)
             * with "keystore failed". Keep the per-connection `false` and
             * the `androidIsEncryption: false` config flag in sync.
             *
             * On Android, `this.dbName` stays sandbox `pos-offline` (writable).
             * Public Lytsoft durability is via backupSandboxToDurable, not this path.
             */
            const tryCreate = async () => {
                this.dbConnection = await sqlite.createConnection(
                    this.dbName,
                    false, // encrypted (must stay false — see JSDoc above)
                    'no-encryption', // mode (plain SQLite, no SQLCipher)
                    1 // version
                );
                await this.dbConnection.open();
                await this.createMobileTables();
            };

            const tryRetrieve = async () => {
                this.dbConnection = await sqlite.retrieveConnection(this.dbName, false);
                // Make sure the retrieved connection is actually open. After
                // a WebView reload Capacitor sometimes hands back a closed
                // handle that throws on the first query.
                try {
                    const isOpen = (await this.dbConnection.isDBOpen()).result;
                    if (!isOpen) {
                        await this.dbConnection.open();
                    }
                } catch (_) {
                    try { await this.dbConnection.open(); } catch (_) { /* best effort */ }
                }
            };

            if (!isConn) {
                try {
                    await tryCreate();
                    console.log('[StorageAdapter] Capacitor SQLite database opened at', this.dbName);
                    console.log('[StorageAdapter] Using Capacitor SQLite');
                } catch (err) {
                    const msg = (err && err.message) || String(err);
                    if (/already exists/i.test(msg)) {
                        console.warn('[StorageAdapter] createConnection said "already exists" — retrieving stale connection');
                        await tryRetrieve();
                        console.log('[StorageAdapter] Using recovered Capacitor SQLite connection');
                    } else {
                        throw err;
                    }
                }
            } else {
                await tryRetrieve();
                console.log('[StorageAdapter] Using existing Capacitor SQLite connection');
            }

            if (Capacitor.getPlatform() === 'android') {
                this.scheduleLytsoftBackup('init');
            }

            // Store reference with methods that match Electron API pattern
            this.db = {
                query: async (sql, params = []) => {
                    try {
                        const result = await safeCall(
                            this.dbConnection,
                            'query',
                            [sql, params],
                            { context: 'db.query' }
                        );
                        return { ok: true, data: result.values || [] };
                    } catch (error) {
                        return { ok: false, error: error.message };
                    }
                },
                run: async (sql, params = []) => {
                    try {
                        const result = await safeCall(
                            this.dbConnection,
                            'run',
                            [sql, params, false],
                            { context: 'db.run' }
                        );
                        return {
                            ok: true,
                            changes: result.changes?.changes || 0,
                            lastInsertRowid: result.changes?.lastId || 0
                        };
                    } catch (error) {
                        return { ok: false, error: error.message };
                    }
                },
                get: async (sql, params = []) => {
                    try {
                        const result = await safeCall(
                            this.dbConnection,
                            'query',
                            [sql, params],
                            { context: 'db.get' }
                        );
                        return {
                            ok: true,
                            data: result.values && result.values.length > 0 ? result.values[0] : undefined
                        };
                    } catch (error) {
                        return { ok: false, error: error.message };
                    }
                },
                execute: async (sql) => {
                    try {
                        await this.dbConnection.execute(sql, false);
                        return { ok: true };
                    } catch (error) {
                        return { ok: false, error: error.message };
                    }
                },
                executeSet: async (set = [], transaction = true, returnMode = 'no') => {
                    try {
                        const result = await safeCall(
                            this.dbConnection,
                            'executeSet',
                            [set, transaction, returnMode, true],
                            { context: 'db.executeSet' }
                        );
                        return {
                            ok: true,
                            changes: result?.changes?.changes || 0,
                            lastInsertRowid: result?.changes?.lastId || 0
                        };
                    } catch (error) {
                        return { ok: false, error: error.message };
                    }
                }
            };
        } catch (error) {
            console.error('[StorageAdapter] Failed to initialize Capacitor SQLite:', error);
            throw new Error(`Mobile SQLite initialization failed: ${error.message} `);
        }
    }

    /**
     * Create database tables for Capacitor SQLite
     * @returns {Promise<void>}
     */
    async createMobileTables() {
        const createTableStatements = [
            // Main key-value store table
            `CREATE TABLE IF NOT EXISTS main_store(
                                                      key TEXT PRIMARY KEY,
                                                      value TEXT NOT NULL,
                                                      created_at DATETIME DEFAULT CURRENT_TIMESTAMP,
                                                      updated_at DATETIME DEFAULT CURRENT_TIMESTAMP
             )`,

            // Admin users table
            `CREATE TABLE IF NOT EXISTS admin_users(
                                                       key TEXT PRIMARY KEY,
                                                       value TEXT NOT NULL,
                                                       created_at DATETIME DEFAULT CURRENT_TIMESTAMP,
                                                       updated_at DATETIME DEFAULT CURRENT_TIMESTAMP
             )`,

            // Sessions table
            `CREATE TABLE IF NOT EXISTS sessions(
                                                    key TEXT PRIMARY KEY,
                                                    value TEXT NOT NULL,
                                                    created_at DATETIME DEFAULT CURRENT_TIMESTAMP,
                                                    updated_at DATETIME DEFAULT CURRENT_TIMESTAMP
             )`,

            // Outbox sales: composite PK so each store can use offline-{pc}-{n}
            `CREATE TABLE IF NOT EXISTS outbox_sales(
                                                        sale_id TEXT NOT NULL,
                                                        date TEXT NOT NULL,
                                                        status TEXT NOT NULL,
                                                        upload_response TEXT,
                                                        upload_date TEXT,
                                                        payload TEXT NOT NULL,
                                                        upload_invoice TEXT,
                                                        attempts INTEGER DEFAULT 0,
                                                        last_attempt_date TEXT,
                                                        store_db TEXT NOT NULL DEFAULT '',
                                                        client_meta TEXT,
                                                        created_at DATETIME DEFAULT CURRENT_TIMESTAMP,
                                                        updated_at DATETIME DEFAULT CURRENT_TIMESTAMP,
                                                        PRIMARY KEY (sale_id, store_db)
             )`,

            // Outbox voids table
            `CREATE TABLE IF NOT EXISTS outbox_voids(
                                                        void_id TEXT PRIMARY KEY,
                                                        date TEXT NOT NULL,
                                                        status TEXT NOT NULL,
                                                        payload TEXT NOT NULL,
                                                        attempts INTEGER DEFAULT 0,
                                                        last_attempt_date TEXT,
                                                        created_at DATETIME DEFAULT CURRENT_TIMESTAMP,
                                                        updated_at DATETIME DEFAULT CURRENT_TIMESTAMP
             )`,

            // Outbox logins table
            `CREATE TABLE IF NOT EXISTS outbox_logins(
                                                         id TEXT PRIMARY KEY,
                                                         username TEXT NOT NULL,
                                                         platform TEXT NOT NULL,
                                                         pc TEXT NOT NULL,
                                                         store_db TEXT NOT NULL,
                                                         date TEXT NOT NULL,
                                                         status TEXT NOT NULL,
                                                         attempts INTEGER DEFAULT 0,
                                                         last_attempt_date TEXT,
                                                         synced_at TEXT,
                                                         created_at DATETIME DEFAULT CURRENT_TIMESTAMP,
                                                         updated_at DATETIME DEFAULT CURRENT_TIMESTAMP
             )`,

            // Invoice number counter table
            `CREATE TABLE IF NOT EXISTS invoice_numbers(
                                                           key TEXT PRIMARY KEY,
                                                           value INTEGER NOT NULL,
                                                           updated_at DATETIME DEFAULT CURRENT_TIMESTAMP
             )`,

            // SQL-first stock storage table
            `CREATE TABLE IF NOT EXISTS stock_items(
                                                       plu TEXT PRIMARY KEY,
                                                       barcode TEXT,
                                                       description TEXT NOT NULL,
                                                       description2 TEXT,
                                                       department TEXT,
                                                       sub_dep TEXT,
                                                       sub_dep2 TEXT,
                                                       selling_incl REAL,
                                                       qty REAL,
                                                       archived INTEGER DEFAULT 0,
                                                       fast_line INTEGER DEFAULT 0,
                                                       has_image INTEGER DEFAULT 0,
                                                       updated_at TEXT NOT NULL,
                                                       payload TEXT NOT NULL
             )`,

            // Local dirty stock table for conflict-safe delta reconciliation
            `CREATE TABLE IF NOT EXISTS stock_dirty(
                                                       plu TEXT PRIMARY KEY,
                                                       local_updated_at TEXT NOT NULL,
                                                       pending_sales INTEGER DEFAULT 0,
                                                       version INTEGER DEFAULT 1,
                                                       last_sale_id TEXT,
                                                       qty_delta REAL DEFAULT 0,
                                                       updated_at DATETIME DEFAULT CURRENT_TIMESTAMP
             )`,

            // Indexes
            `CREATE INDEX IF NOT EXISTS idx_outbox_sales_status ON outbox_sales(status)`,
            `CREATE INDEX IF NOT EXISTS idx_outbox_sales_date ON outbox_sales(date)`,
            `CREATE INDEX IF NOT EXISTS idx_outbox_sales_store_db ON outbox_sales(store_db)`,
            `CREATE INDEX IF NOT EXISTS idx_outbox_voids_status ON outbox_voids(status)`,
            `CREATE INDEX IF NOT EXISTS idx_outbox_logins_status ON outbox_logins(status)`,
            `CREATE INDEX IF NOT EXISTS idx_outbox_logins_date ON outbox_logins(date)`,
            `CREATE INDEX IF NOT EXISTS idx_outbox_logins_store_db ON outbox_logins(store_db)`,
            `CREATE INDEX IF NOT EXISTS idx_stock_barcode ON stock_items(barcode)`,
            `CREATE INDEX IF NOT EXISTS idx_stock_department ON stock_items(department, sub_dep, sub_dep2)`,
            `CREATE INDEX IF NOT EXISTS idx_stock_updated_at ON stock_items(updated_at)`,
            `CREATE INDEX IF NOT EXISTS idx_stock_fast_line ON stock_items(fast_line, archived)`,
            `CREATE INDEX IF NOT EXISTS idx_stock_dirty_pending ON stock_dirty(pending_sales, local_updated_at)`,
            `CREATE TABLE IF NOT EXISTS item_barcodes(
                                                        barcode TEXT PRIMARY KEY,
                                                        stock_code TEXT NOT NULL
             )`,
            `CREATE INDEX IF NOT EXISTS idx_item_barcodes_stock ON item_barcodes(stock_code)`
        ];

        for (const sql of createTableStatements) {
            try {
                await this.dbConnection.execute(sql, false);
            } catch (error) {
                console.error(`[StorageAdapter] Error creating table: ${sql.substring(0, 50)}...`, error);
                throw error;
            }
        }

        await this.migrateOutboxSalesCompositePkMobile();

        console.log('[StorageAdapter] Capacitor SQLite tables created successfully');
    }

    /**
     * Mobile: migrate outbox_sales to PRIMARY KEY (sale_id, store_db) when still sale_id-only.
     */
    async migrateOutboxSalesCompositePkMobile() {
        try {
            const info = await this.dbConnection.query('PRAGMA table_info(outbox_sales)');
            const cols = Array.isArray(info?.values)
                ? info.values.map((row) => {
                    // Capacitor may return array rows or objects
                    if (Array.isArray(row)) {
                        return { name: row[1], pk: row[5] };
                    }
                    return { name: row.name, pk: row.pk };
                })
                : [];
            if (cols.length === 0) return;

            const pkCols = cols
                .filter((c) => Number(c.pk) > 0)
                .sort((a, b) => Number(a.pk) - Number(b.pk))
                .map((c) => c.name);

            const isComposite =
                pkCols.length === 2 &&
                pkCols[0] === 'sale_id' &&
                pkCols[1] === 'store_db';
            if (isComposite) return;

            console.log('[StorageAdapter] Migrating mobile outbox_sales to composite PK');
            const steps = [
                `CREATE TABLE IF NOT EXISTS outbox_sales_v2 (
                    sale_id TEXT NOT NULL,
                    date TEXT NOT NULL,
                    status TEXT NOT NULL,
                    upload_response TEXT,
                    upload_date TEXT,
                    payload TEXT NOT NULL,
                    upload_invoice TEXT,
                    attempts INTEGER DEFAULT 0,
                    last_attempt_date TEXT,
                    store_db TEXT NOT NULL DEFAULT '',
                    client_meta TEXT,
                    created_at DATETIME DEFAULT CURRENT_TIMESTAMP,
                    updated_at DATETIME DEFAULT CURRENT_TIMESTAMP,
                    PRIMARY KEY (sale_id, store_db)
                )`,
                `INSERT OR IGNORE INTO outbox_sales_v2
                    (sale_id, date, status, upload_response, upload_date, payload, upload_invoice,
                     attempts, last_attempt_date, store_db, client_meta, created_at, updated_at)
                 SELECT
                    sale_id, date, status, upload_response, upload_date, payload, upload_invoice,
                    attempts, last_attempt_date, COALESCE(store_db, ''), client_meta, created_at, updated_at
                 FROM outbox_sales`,
                `DROP TABLE outbox_sales`,
                `ALTER TABLE outbox_sales_v2 RENAME TO outbox_sales`,
                `CREATE INDEX IF NOT EXISTS idx_outbox_sales_status ON outbox_sales(status)`,
                `CREATE INDEX IF NOT EXISTS idx_outbox_sales_date ON outbox_sales(date)`,
                `CREATE INDEX IF NOT EXISTS idx_outbox_sales_store_db ON outbox_sales(store_db)`
            ];
            for (const sql of steps) {
                await this.dbConnection.execute(sql, false);
            }
            console.log('[StorageAdapter] Mobile outbox_sales composite PK migration done');
        } catch (e) {
            console.error('[StorageAdapter] Mobile outbox_sales migration failed:', e);
            // Non-fatal if PRAGMA shape differs; composite create on fresh installs still works
        }
    }

    /**
     * Wait for window.database to be ready (Electron only)
     * Tests actual database readiness, not just API availability
     * @returns {Promise<boolean>}
     */
    async waitForDatabase() {
        const maxAttempts = 50; // 5 seconds max - reduced from 20s for faster UX
        const delay = 100; // 100ms between attempts
        let lastError = null;

        for (let i = 0; i < maxAttempts; i++) {
            const databaseApi = window?.database;
            if (!databaseApi) {
                // API not exposed yet, wait
                await new Promise(resolve => setTimeout(resolve, delay));
                continue;
            }

            const hasCoreMethods =
                typeof databaseApi.query === 'function' &&
                typeof databaseApi.run === 'function' &&
                typeof databaseApi.get === 'function';

            if (!hasCoreMethods) {
                lastError = new Error('window.database is missing required query/run/get methods');
                await new Promise(resolve => setTimeout(resolve, delay));
                continue;
            }

            // Preferred readiness probe for modern preload bridge
            try {
                if (typeof databaseApi.health === 'function') {
                    const result = await databaseApi.health();

                    if (result === true || (result && result.ok === true)) {
                        return true;
                    }

                    if (result && result.ok === false && i % 10 === 0) {
                        console.log(`[StorageAdapter] Database initializing (attempt ${i + 1})...`);
                    }
                }
            } catch (error) {
                lastError = error;
                if (i % 10 === 0) {
                    console.log(`[StorageAdapter] Waiting for database (attempt ${i + 1})...`);
                }
            }

            // Compatibility probe for legacy bridges without health()
            try {
                const probe = await databaseApi.query('SELECT 1 as test', []);
                if (probe && probe.ok === true) {
                    return true;
                }
            } catch (error) {
                lastError = error;
            }

            await new Promise(resolve => setTimeout(resolve, delay));
        }

        console.error('[StorageAdapter] Timeout waiting for database initialization', lastError);
        return false;
    }

    /**
     * Initialize storage
     */
    async init() {
        if (this.db) return; // Already initialized
        if (this.initPromise) {
            await this.initPromise;
            return;
        }

        this.initPromise = (async () => {
            switch (this.platform) {
                case 'electron': {
                    // Hybrid approach: Use IndexedDB for hot data (renderer) and SQLite for persistence (main)
                    try {
                        await this.initIndexedDB();
                        this.idb = this.db; // Save reference to IndexedDB
                        this.db = null; // Clear so we can capture SQLite next
                    } catch (e) {
                        console.error('[StorageAdapter] Failed to init local IDB for hybrid mode:', e);
                    }

                    // Wait for window.database to be ready
                    const isReady = await this.waitForDatabase();
                    if (isReady) {
                        this.sqlite = window.database; // Save reference to SQLite IPC
                        this.db = this.createHybridDB();
                    } else if (this.idb) {
                        // Keep app functional if SQLite bridge is unavailable
                        this.platform = 'indexeddb';
                        this.db = this.idb;
                        this.idb = null;
                        console.warn('[StorageAdapter] Falling back to IndexedDB: Electron database bridge unavailable');
                    } else {
                        throw new Error('[StorageAdapter] Failed to initialize any storage backend');
                    }
                    break;
                }

                case 'mobile': {
                    // Always use SQLite on Capacitor/mobile for consistency with Electron
                    console.log('[StorageAdapter] Capacitor detected - initializing SQLite');
                    await this.initMobileSQLite();
                    break;
                }

                case 'indexeddb':
                    // IndexedDB (web/development)
                    await this.initIndexedDB();
                    console.log('[StorageAdapter] Using IndexedDB');
                    break;
            }
        })();

        try {
            await this.initPromise;
        } finally {
            this.initPromise = null;
        }
    }

    /**
     * Execute a SELECT query
     * @param {string} sql SQL query
     * @param {Array} params Query parameters
     * @returns {Promise<Array>} Query results
     */
    async query(sql, params = []) {
        await this.init();

        switch (this.platform) {
            case 'electron': {
                const result = await this.db.query(sql, params);
                if (!result.ok) {
                    throw new Error(result.error);
                }
                return result.data;
            }

            case 'mobile': {
                const result = await this.db.query(sql, params);
                if (!result.ok) {
                    throw new Error(result.error || 'Query failed');
                }
                return result.data;
            }

            case 'indexeddb':
                // Map SQL to IndexedDB operations
                return this.executeIndexedDB(sql, params);
        }
    }

    /**
     * Execute an INSERT/UPDATE/DELETE statement
     * @param {string} sql SQL statement
     * @param {Array} params Statement parameters
     * @returns {Promise<Object>} Execution result with changes and lastInsertRowid
     */
    async run(sql, params = []) {
        await this.init();

        switch (this.platform) {
            case 'electron': {
                const result = await this.db.run(sql, params);
                if (!result.ok) {
                    throw new Error(result.error);
                }
                return { changes: result.changes, lastInsertRowid: result.lastInsertRowid };
            }

            case 'mobile': {
                const result = await this.db.run(sql, params);
                if (!result.ok) {
                    throw new Error(result.error || 'Run failed');
                }
                return { changes: result.changes, lastInsertRowid: result.lastInsertRowid };
            }

            case 'indexeddb':
                // Map SQL to IndexedDB operations
                return this.executeIndexedDB(sql, params);
        }
    }

    /**
     * Execute many INSERT/UPDATE/DELETE statements efficiently.
     * @param {Array<{sql:string, params?:Array}>} statements
     * @param {Object} options
     * @param {boolean} options.transaction Wrap in a transaction when supported
     * @returns {Promise<Object>} Execution result with changes and lastInsertRowid
     */
    async runBatch(statements = [], options = {}) {
        await this.init();

        const batch = (Array.isArray(statements) ? statements : [])
            .filter(entry => entry && typeof entry.sql === 'string');
        if (batch.length === 0) {
            return { changes: 0, lastInsertRowid: 0 };
        }

        const requestedTransaction = options?.transaction !== false;

        const runSequentially = async () => {
            let totalChanges = 0;
            let lastInsertRowid = 0;
            for (const entry of batch) {
                const result = await this.run(entry.sql, Array.isArray(entry.params) ? entry.params : []);
                totalChanges += Number(result?.changes || 0);
                if (Number(result?.lastInsertRowid || 0) > 0) {
                    lastInsertRowid = Number(result.lastInsertRowid);
                }
            }
            return { changes: totalChanges, lastInsertRowid };
        };

        switch (this.platform) {
            case 'mobile': {
                if (typeof this.db?.executeSet !== 'function') {
                    return runSequentially();
                }

                const set = batch.map(entry => ({
                    statement: entry.sql,
                    values: Array.isArray(entry.params) ? entry.params : []
                }));

                let result = await this.db.executeSet(set, requestedTransaction, 'no');
                if (!result.ok && requestedTransaction) {
                    const message = String(result.error || '').toLowerCase();
                    if (message.includes('within a transaction')) {
                        result = await this.db.executeSet(set, false, 'no');
                    }
                }

                if (!result.ok) {
                    throw new Error(result.error || 'executeSet failed');
                }
                return {
                    changes: result.changes || 0,
                    lastInsertRowid: result.lastInsertRowid || 0
                };
            }

            case 'electron':
            case 'indexeddb':
            default:
                return runSequentially();
        }
    }

    /**
     * Get a single row
     * @param {string} sql SQL query
     * @param {Array} params Query parameters
     * @returns {Promise<Object|undefined>} Single row or undefined
     */
    async get(sql, params = []) {
        await this.init();

        switch (this.platform) {
            case 'electron': {
                const result = await this.db.get(sql, params);
                if (!result.ok) {
                    throw new Error(result.error);
                }
                return result.data;
            }

            case 'mobile': {
                const result = await this.db.get(sql, params);
                if (!result.ok) {
                    throw new Error(result.error || 'Get failed');
                }
                return result.data;
            }

            case 'indexeddb':
                // Map SQL to IndexedDB operations
                return this.executeIndexedDB(sql, params);
        }
    }

    /**
     * Get value by key from a specific store (Storage.js compatibility)
     * @param {string} key Key to retrieve
     * @param {string} storeName Store name (main_store, admin_users, sessions)
     * @returns {Promise<any>} Value or undefined
     */
    async getValue(key, storeName = 'main_store') {
        await this.init();

        switch (this.platform) {
            case 'electron': {
                const result = await this.db.get(
                    `SELECT value FROM ${storeName} WHERE key = ? `,
                    [key]
                );
                if (!result.ok) {
                    throw new Error(result.error);
                }

                if (!result.data) {
                    return undefined;
                }

                const value = result.data.value;

                // Try to parse JSON strings back to objects
                if (typeof value === 'string') {
                    // Check if it looks like JSON (starts with { or [)
                    if ((value.startsWith('{') && value.endsWith('}')) ||
                        (value.startsWith('[') && value.endsWith(']'))) {
                        try {
                            return JSON.parse(value);
                        } catch (e) {
                            // Not valid JSON, return as-is
                            return value;
                        }
                    }
                }

                return value;
            }

            case 'mobile': {
                const result = await this.db.get(
                    `SELECT value FROM ${storeName} WHERE key = ? `,
                    [key]
                );
                if (!result.ok) {
                    throw new Error(result.error || 'Get failed');
                }

                if (!result.data) {
                    return undefined;
                }

                const value = result.data.value;

                // Try to parse JSON strings back to objects
                if (typeof value === 'string') {
                    // Check if it looks like JSON (starts with { or [)
                    if ((value.startsWith('{') && value.endsWith('}')) ||
                        (value.startsWith('[') && value.endsWith(']'))) {
                        try {
                            return JSON.parse(value);
                        } catch (e) {
                            // Not valid JSON, return as-is
                            return value;
                        }
                    }
                }

                return value;
            }

            case 'indexeddb': {
                // Map store names to IndexedDB object stores
                const idbStoreName = this.mapStoreNameToIDB(storeName);
                const tx = this.db.transaction(idbStoreName, 'readonly');
                const store = tx.objectStore(idbStoreName);
                return await store.get(key);
            }
        }
    }

    /**
     * Set value by key in a specific store (Storage.js compatibility)
     * @param {string} key Key to set
     * @param {any} value Value to store
     * @param {string} storeName Store name (main_store, admin_users, sessions)
     * @returns {Promise<void>}
     */
    async setValue(key, value, storeName = 'main_store') {
        await this.init();

        switch (this.platform) {
            case 'electron': {
                // Convention: setValue(key, null) means "delete the row".
                // The SQLite schema declares `value TEXT NOT NULL`, so an
                // INSERT/UPDATE with a null value would throw at runtime.
                // Routing null through DELETE here matches the
                // key-value-store idiom and lets callers like
                // `Storage.clearCurrentSession` (which previously tripped
                // this NOT NULL constraint) succeed without each call
                // site having to know about the schema.
                if (value === null) {
                    const result = await this.db.run(
                        `DELETE FROM ${storeName} WHERE key = ?`,
                        [key]
                    );
                    if (!result.ok) {
                        throw new Error(result.error);
                    }
                    break;
                }

                // SQLite only accepts primitives (numbers, strings, bigints, buffers, null)
                // Serialize complex objects to JSON strings
                let serializedValue = value;
                if (value !== null && value !== undefined && typeof value === 'object') {
                    serializedValue = JSON.stringify(value);
                } else if (typeof value !== 'string' && typeof value !== 'number' && typeof value !== 'bigint' && value !== null) {
                    serializedValue = String(value);
                }

                const result = await this.db.run(
                    `INSERT INTO ${storeName} (key, value, updated_at)
                     VALUES(?, ?, CURRENT_TIMESTAMP)
                         ON CONFLICT(key) DO UPDATE SET value = ?, updated_at = CURRENT_TIMESTAMP`,
                    [key, serializedValue, serializedValue]
                );
                if (!result.ok) {
                    throw new Error(result.error);
                }
                break;
            }

            case 'mobile': {
                // Convention: setValue(key, null) means "delete the row".
                // See the matching note in the `electron` branch above -
                // the SQLite schema's NOT NULL constraint on `value`
                // means we must route null through DELETE rather than
                // INSERT/UPDATE.
                if (value === null) {
                    const result = await this.db.run(
                        `DELETE FROM ${storeName} WHERE key = ?`,
                        [key]
                    );
                    if (!result.ok) {
                        throw new Error(result.error || 'SetValue delete failed');
                    }
                    break;
                }

                // SQLite only accepts primitives (numbers, strings, bigints, buffers, null)
                // Serialize complex objects to JSON strings
                let serializedValue = value;
                if (value !== null && value !== undefined && typeof value === 'object') {
                    serializedValue = JSON.stringify(value);
                } else if (typeof value !== 'string' && typeof value !== 'number' && typeof value !== 'bigint' && value !== null) {
                    serializedValue = String(value);
                }

                const result = await this.db.run(
                    `INSERT INTO ${storeName} (key, value, updated_at)
                     VALUES(?, ?, datetime('now'))
                         ON CONFLICT(key) DO UPDATE SET value = ?, updated_at = datetime('now')`,
                    [key, serializedValue, serializedValue]
                );
                if (!result.ok) {
                    throw new Error(result.error || 'SetValue failed');
                }
                break;
            }

            case 'indexeddb': {
                // Map store names to IndexedDB object stores
                const idbStoreName = this.mapStoreNameToIDB(storeName);
                const tx = this.db.transaction(idbStoreName, 'readwrite');
                const store = tx.objectStore(idbStoreName);
                await store.put(value, key);
                await tx.done;
                break;
            }
        }
    }

    /**
     * Initialize IndexedDB database and object stores
     */
    async initIndexedDB() {
        try {
            const { openDB } = await import('idb');
            // v3: outbox_sales keys are store_db + NUL + sale_id (composite uniqueness)
            this.db = await openDB('pos-db', 3, {
                upgrade(db) {
                    if (!db.objectStoreNames.contains('main')) db.createObjectStore('main');
                    if (!db.objectStoreNames.contains('adminUsers')) db.createObjectStore('adminUsers');
                    if (!db.objectStoreNames.contains('sessions')) db.createObjectStore('sessions');
                    if (!db.objectStoreNames.contains('outbox_sales')) db.createObjectStore('outbox_sales');
                    if (!db.objectStoreNames.contains('outbox_voids')) db.createObjectStore('outbox_voids');
                    if (!db.objectStoreNames.contains('outbox_logins')) db.createObjectStore('outbox_logins');
                    if (!db.objectStoreNames.contains('invoice_number')) db.createObjectStore('invoice_number');
                }
            });
            await this.migrateIdbOutboxCompositeKeys();
        } catch (e) {
            console.error('[StorageAdapter] Failed to initialize IndexedDB:', e);
            throw e;
        }
    }

    /**
     * IDB outbox key: store_db + NUL + sale_id (NUL never appears in store ids).
     */
    outboxIdbKey(saleId, storeDb) {
        return `${storeDb || ''}\0${saleId || ''}`;
    }

    /**
     * Re-key legacy outbox rows that used sale_id alone as the IDB key.
     */
    async migrateIdbOutboxCompositeKeys() {
        if (!this.db || !this.db.objectStoreNames.contains('outbox_sales')) return;
        try {
            const tx = this.db.transaction('outbox_sales', 'readwrite');
            const store = tx.store;
            const keys = await store.getAllKeys();
            const values = await store.getAll();
            for (let i = 0; i < keys.length; i++) {
                const key = keys[i];
                const row = values[i];
                if (typeof key === 'string' && key.includes('\0')) continue;
                const saleId = row?.sale_id || key;
                const storeDb = row?.store_db != null ? String(row.store_db) : '';
                const newKey = this.outboxIdbKey(saleId, storeDb);
                const nextRow = {
                    ...(row && typeof row === 'object' ? row : {}),
                    sale_id: saleId,
                    store_db: storeDb
                };
                await store.delete(key);
                await store.put(nextRow, newKey);
            }
            await tx.done;
        } catch (e) {
            console.warn('[StorageAdapter] IDB outbox key migration skipped/failed:', e);
        }
    }

    /**
     * Map SQL table names to IndexedDB store names
     * @param {string} tableName SQL table name
     * @returns {string} IndexedDB store name
     */
    mapStoreNameToIDB(tableName) {
        const mapping = {
            'main_store': 'main',
            'admin_users': 'adminUsers',
            'sessions': 'sessions',
            'outbox_sales': 'outbox_sales',
            'outbox_voids': 'outbox_voids',
            'outbox_logins': 'outbox_logins',
            'invoice_numbers': 'invoice_number'
        };
        return mapping[tableName] || tableName;
    }

    /**
     * Execute IndexedDB operations (fallback for development)
     * This is a simplified mapper for basic operations
     * @param {string} sql SQL statement
     * @param {Array} params Parameters
     * @returns {Promise<any>} Result
     */
    async executeIndexedDB(sql, params = []) {
        // Parse SQL to determine operation
        const sqlLower = sql.toLowerCase().trim();

        // Extract table name
        let tableName;
        if (sqlLower.includes('from ')) {
            tableName = sql.match(/from\s+(\w+)/i)?.[1];
        } else if (sqlLower.includes('into ')) {
            tableName = sql.match(/into\s+(\w+)/i)?.[1];
        } else if (sqlLower.includes('update ')) {
            tableName = sql.match(/update\s+(\w+)/i)?.[1];
        } else if (sqlLower.startsWith('delete from ')) {
            tableName = sql.match(/delete\s+from\s+(\w+)/i)?.[1];
        }

        if (!tableName) {
            console.warn('[StorageAdapter] Could not parse table name from SQL:', sql);
            return [];
        }

        const idbStoreName = this.mapStoreNameToIDB(tableName);
        const tx = this.db.transaction(idbStoreName, sqlLower.startsWith('select') ? 'readonly' : 'readwrite');
        const store = tx.objectStore(idbStoreName);

        const sortRowsByDate = (rows = [], desc = false) => {
            const sorted = [...rows].sort((a, b) => {
                const aTs = new Date(a?.date || 0).getTime();
                const bTs = new Date(b?.date || 0).getTime();
                return aTs - bTs;
            });
            return desc ? sorted.reverse() : sorted;
        };

        const statusInPendingTimeout = (row) => row?.status === 'pending' || row?.status === 'timeout';

        const getByPrimaryKey = async (key) => {
            if (key === undefined || key === null) return undefined;
            return store.get(key);
        };

        // Simple operations mapping
        if (sqlLower.startsWith('select')) {
            if (sqlLower.includes('count(*)')) {
                let rows = await store.getAll();
                if (sqlLower.includes("where status in ('pending', 'timeout')")) {
                    rows = rows.filter(statusInPendingTimeout);
                } else if (sqlLower.includes('where status = ?')) {
                    const status = params[0];
                    rows = rows.filter(r => r?.status === status);
                }
                return { count: rows.length };
            }

            if (sqlLower.includes('where key = ?')) {
                const row = await getByPrimaryKey(params[0]);
                if (!row) return undefined;
                if (sqlLower.includes('select value')) return { value: row.value };
                return row;
            }

            if (sqlLower.includes('where sale_id = ? and store_db = ?')) {
                const row = await getByPrimaryKey(this.outboxIdbKey(params[0], params[1]));
                if (!row) return undefined;
                if (sqlLower.includes('select attempts')) {
                    return { attempts: row.attempts || 0 };
                }
                return row;
            }

            if (sqlLower.includes('where sale_id = ?') || sqlLower.includes('where void_id = ?') || sqlLower.includes('where id = ?')) {
                // Legacy sale_id-only: first match (prefer compound key scan)
                if (tableName === 'outbox_sales') {
                    const all = await store.getAll();
                    const row = all.find((r) => r?.sale_id === params[0]);
                    if (!row) return undefined;
                    if (sqlLower.includes('select attempts')) {
                        return { attempts: row.attempts || 0 };
                    }
                    return row;
                }
                const row = await getByPrimaryKey(params[0]);
                if (!row) return undefined;

                if (sqlLower.includes('select attempts, payload')) {
                    return { attempts: row.attempts || 0, payload: row.payload };
                }
                if (sqlLower.includes('select attempts')) {
                    return { attempts: row.attempts || 0 };
                }
                if (sqlLower.includes('select payload')) {
                    return { payload: row.payload };
                }
                return row;
            }

            let rows = await store.getAll();

            if (sqlLower.includes('where store_db = ?') && !sqlLower.includes('sale_id')) {
                rows = rows.filter((r) => String(r?.store_db || '') === String(params[0] || ''));
            }

            if (sqlLower.includes("where status in ('pending', 'timeout')")) {
                rows = rows.filter(statusInPendingTimeout);
            }

            if (sqlLower.includes("or (status = 'sending' and last_attempt_date < ?)")) {
                const threshold = params[0];
                rows = rows.filter(r =>
                    statusInPendingTimeout(r) ||
                    (r?.status === 'sending' && r?.last_attempt_date && r.last_attempt_date < threshold)
                );
            }

            if (sqlLower.includes('order by date asc')) {
                rows = sortRowsByDate(rows, false);
            } else if (sqlLower.includes('order by date desc')) {
                rows = sortRowsByDate(rows, true);
            }

            if (sqlLower.includes('limit ?')) {
                const limit = Number(params[params.length - 1]);
                if (Number.isFinite(limit) && limit > 0) {
                    rows = rows.slice(0, limit);
                }
            }

            return rows;
        } else if (sqlLower.startsWith('insert')) {
            if (tableName === 'outbox_sales') {
                const row = {
                    sale_id: params[0],
                    date: params[1],
                    status: params[2],
                    upload_response: params[3],
                    upload_date: params[4],
                    payload: params[5],
                    upload_invoice: params[6],
                    attempts: params[7],
                    last_attempt_date: params[8],
                    store_db: params[9] != null ? String(params[9]) : '',
                    client_meta: params[10]
                };
                const key = this.outboxIdbKey(row.sale_id, row.store_db);
                const existing = await store.get(key);
                if (existing) {
                    throw new Error('UNIQUE constraint failed: outbox_sales.sale_id');
                }
                await store.put(row, key);
                await tx.done;
                return { changes: 1, lastInsertRowid: row.sale_id };
            }

            if (tableName === 'outbox_voids') {
                const row = {
                    void_id: params[0],
                    date: params[1],
                    status: params[2],
                    payload: params[3],
                    attempts: params[4],
                    last_attempt_date: params[5]
                };
                await store.put(row, row.void_id);
                await tx.done;
                return { changes: 1, lastInsertRowid: row.void_id };
            }

            if (tableName === 'outbox_logins') {
                const row = {
                    id: params[0],
                    username: params[1],
                    platform: params[2],
                    pc: params[3],
                    store_db: params[4],
                    date: params[5],
                    status: params[6],
                    attempts: params[7],
                    last_attempt_date: params[8],
                    synced_at: params[9]
                };
                await store.put(row, row.id);
                await tx.done;
                return { changes: 1, lastInsertRowid: row.id };
            }

            if (sqlLower.includes('on conflict(key) do update')) {
                const key = params[0];
                const value = params[1];
                const existing = await store.get(key);
                const row = {
                    ...(existing && typeof existing === 'object' ? existing : {}),
                    key,
                    value,
                    updated_at: new Date().toISOString()
                };
                await store.put(row, key);
                await tx.done;
                return { changes: 1, lastInsertRowid: key };
            }

            // Generic insert fallback
            if (params.length >= 2 && typeof params[0] === 'string') {
                const row = { key: params[0], value: params[1], updated_at: new Date().toISOString() };
                await store.put(row, params[0]);
                await tx.done;
                return { changes: 1, lastInsertRowid: params[0] };
            }

            await store.put(params[0]);
            await tx.done;
            return { changes: 1 };
        } else if (sqlLower.startsWith('update')) {
            if (tableName === 'outbox_sales') {
                let saleId;
                let storeDb;
                let row;
                let idbKey;
                if (sqlLower.includes('where sale_id = ? and store_db = ?')) {
                    saleId = params[params.length - 2];
                    storeDb = params[params.length - 1];
                    idbKey = this.outboxIdbKey(saleId, storeDb);
                    row = await store.get(idbKey);
                } else {
                    saleId = params[params.length - 1];
                    const all = await store.getAll();
                    row = all.find((r) => r?.sale_id === saleId);
                    if (row) {
                        idbKey = this.outboxIdbKey(row.sale_id, row.store_db);
                    }
                }
                if (!row || !idbKey) return { changes: 0 };

                if (sqlLower.includes("set status = 'sending'")) {
                    row.status = 'sending';
                    row.attempts = Number(row.attempts || 0) + 1;
                    row.last_attempt_date = params[0];
                } else if (sqlLower.includes("set status = 'sent'")) {
                    row.status = 'sent';
                    row.upload_response = params[0];
                    row.upload_date = params[1];
                    row.upload_invoice = params[2];
                } else if (sqlLower.includes("set status = ?,") && sqlLower.includes('upload_response = ?')) {
                    row.status = params[0];
                    row.upload_response = params[1];
                    row.last_attempt_date = params[2];
                } else if (sqlLower.includes("set attempts = attempts + 1") && sqlLower.includes("status = 'pending'")) {
                    row.attempts = Number(row.attempts || 0) + 1;
                    row.status = 'pending';
                    row.last_attempt_date = params[0];
                } else if (sqlLower.includes('set payload = ?') && sqlLower.includes("status = 'pending'") && sqlLower.includes('attempts = 0')) {
                    row.payload = params[0];
                    if (sqlLower.includes('client_meta = ?')) {
                        row.client_meta = params[1];
                    }
                    row.status = 'pending';
                    row.attempts = 0;
                    row.upload_response = null;
                    row.upload_date = null;
                } else {
                    return { changes: 0 };
                }

                row.updated_at = new Date().toISOString();
                await store.put(row, idbKey);
                await tx.done;
                return { changes: 1 };
            }

            if (tableName === 'outbox_voids') {
                const voidId = params[params.length - 1];
                const row = await store.get(voidId);
                if (!row) return { changes: 0 };

                if (sqlLower.includes("set status = 'sending'")) {
                    row.status = 'sending';
                    row.attempts = Number(row.attempts || 0) + 1;
                    row.last_attempt_date = params[0];
                } else if (sqlLower.includes("set status = 'sent'")) {
                    row.status = 'sent';
                    row.payload = params[0];
                } else if (sqlLower.includes("set status = ?,") && sqlLower.includes('payload = ?')) {
                    row.status = params[0];
                    row.payload = params[1];
                    row.last_attempt_date = params[2];
                } else if (sqlLower.includes("set attempts = attempts + 1") && sqlLower.includes("status = 'pending'")) {
                    row.attempts = Number(row.attempts || 0) + 1;
                    row.status = 'pending';
                    row.last_attempt_date = params[0];
                } else {
                    return { changes: 0 };
                }

                row.updated_at = new Date().toISOString();
                await store.put(row, voidId);
                await tx.done;
                return { changes: 1 };
            }

            if (tableName === 'outbox_logins') {
                const loginId = params[params.length - 1];
                const row = await store.get(loginId);
                if (!row) return { changes: 0 };

                if (sqlLower.includes("set status = 'sending'")) {
                    row.status = 'sending';
                    row.attempts = Number(row.attempts || 0) + 1;
                    row.last_attempt_date = params[0];
                } else if (sqlLower.includes("set status = 'sent'")) {
                    row.status = 'sent';
                    row.synced_at = params[0];
                } else if (sqlLower.includes('set status = ?')) {
                    row.status = params[0];
                    if (sqlLower.includes('attempts = attempts + 1')) {
                        row.attempts = Number(row.attempts || 0) + 1;
                    }
                    if (sqlLower.includes('last_attempt_date = ?')) {
                        row.last_attempt_date = params[1];
                    }
                } else {
                    return { changes: 0 };
                }

                row.updated_at = new Date().toISOString();
                await store.put(row, loginId);
                await tx.done;
                return { changes: 1 };
            }

            return { changes: 0 };
        } else if (sqlLower.startsWith('delete')) {
            if (tableName === 'outbox_sales' && sqlLower.includes('where sale_id = ? and store_db = ?')) {
                const key = this.outboxIdbKey(params[0], params[1]);
                await store.delete(key);
                await tx.done;
                return { changes: 1 };
            }

            if (sqlLower.includes('where key = ?') || sqlLower.includes('where sale_id = ?') || sqlLower.includes('where void_id = ?') || sqlLower.includes('where id = ?')) {
                if (tableName === 'outbox_sales' && params[0] != null) {
                    const all = await store.getAll();
                    const keys = await store.getAllKeys();
                    let changes = 0;
                    for (let i = 0; i < all.length; i++) {
                        if (all[i]?.sale_id === params[0]) {
                            await store.delete(keys[i]);
                            changes += 1;
                        }
                    }
                    await tx.done;
                    return { changes };
                }
                if (params[0] !== undefined && params[0] !== null) {
                    await store.delete(params[0]);
                    await tx.done;
                    return { changes: 1 };
                }
                await tx.done;
                return { changes: 0 };
            }

            if (tableName === 'outbox_sales' && sqlLower.includes("where status = 'sent' and date < ?")) {
                const threshold = params[0];
                const rows = await store.getAll();
                const keys = await store.getAllKeys();
                let changes = 0;
                for (let i = 0; i < rows.length; i++) {
                    const row = rows[i];
                    if (row?.status === 'sent' && row?.date && row.date < threshold) {
                        await store.delete(keys[i]);
                        changes += 1;
                    }
                }
                await tx.done;
                return { changes };
            }

            if (params[0]) {
                await store.delete(params[0]);
                await tx.done;
                return { changes: 1 };
            }
            await tx.done;
            return { changes: 0 };
        }

        return [];
    }
    /**
     * Create a hybrid database proxy that routes hot data to IndexedDB
     * and persistent data to SQLite
     */
    createHybridDB() {
        const hotDataKeys = new Set([
            'default_store',
            'current_user',
            'device_settings',
            'discountSpecials',
            'multiSpecials',
            'comboSpecials',
            'base_currency',
            'taxLabels',
            'systemSettings',
            'stock_setting',
            'payment_methods',
            'all_users',
            'securities',
            'kazangProductList',
            'registration_details'
        ]);

        return {
            get: async (sql, params = []) => {
                // Check if this is a hot key request
                // SQL format: SELECT value FROM table WHERE key = ?
                // params: [key]
                if (this.idb && params.length > 0 && typeof params[0] === 'string' && hotDataKeys.has(params[0])) {
                    try {
                        // Extract table name
                        const match = sql.match(/FROM\s+(\w+)/i);
                        const tableName = match ? match[1] : 'main_store';
                        const idbStoreName = this.mapStoreNameToIDB(tableName);

                        // Try to get from IDB
                        const val = await this.idb.get(idbStoreName, params[0]);

                        // If found, return in SQLite result format
                        if (val !== undefined) {
                            return { ok: true, data: { value: val } };
                        }
                        // If not found in IDB (e.g. before hydration), fallback to SQLite
                    } catch (e) {
                        console.warn('[StorageAdapter] Hybrid IDB get failed, falling back to SQLite:', e);
                    }
                }

                // Fallback to SQLite (IPC)
                return this.sqlite.get(sql, params);
            },

            run: async (sql, params = []) => {
                // Execute on SQLite first (Source of Truth)
                const result = await this.sqlite.run(sql, params);

                // If successful and is hot key, update IDB
                if (result.ok && this.idb && params.length > 0 && typeof params[0] === 'string' && hotDataKeys.has(params[0])) {
                    try {
                        const key = params[0];
                        // Params for setValue/run are [key, val, val] (for ON CONFLICT update)
                        // Second param is the serialized value
                        const value = params[1];

                        const match = sql.match(/INTO\s+(\w+)/i);
                        const tableName = match ? match[1] : 'main_store';
                        const idbStoreName = this.mapStoreNameToIDB(tableName);

                        await this.idb.put(idbStoreName, value, key);
                    } catch (e) {
                        console.error('[StorageAdapter] Hybrid IDB write failed:', e);
                    }
                }

                return result;
            },

            query: (sql, params) => this.sqlite.query(sql, params),
            execute: (sql) => this.sqlite.execute(sql)
        };
    }

    /**
     * Hydrate IndexedDB with hot data from SQLite (Electron Only)
     * This ensures the renderer cache is warm on startup
     */
    async hydrateHotData() {
        // Only run if we are in hybrid mode (both DBs available)
        if (!this.idb || !this.sqlite) return;

        console.log('[StorageAdapter] Hydrating hot data to IndexedDB...');
        const hotDataKeys = [
            'default_store',
            'current_user',
            'device_settings',
            'discountSpecials',
            'multiSpecials',
            'comboSpecials',
            'base_currency',
            'taxLabels',
            'systemSettings',
            'stock_setting',
            'payment_methods',
            'all_users',
            'securities',
            'kazangProductList',
            'registration_details'
        ];

        const idbStoreName = this.mapStoreNameToIDB('main_store');
        let count = 0;

        // Process in parallel
        await Promise.all(hotDataKeys.map(async (key) => {
            try {
                // Direct SQLite read (bypass hybrid generic get)
                const res = await this.sqlite.get(
                    `SELECT value FROM main_store WHERE key = ?`, [key]
                );

                if (res.ok && res.data) {
                    const value = res.data.value; // This is the serialized string
                    if (value) {
                        await this.idb.put(idbStoreName, value, key);
                        count++;
                    }
                }
            } catch (e) {
                // Log but continue
                console.warn(`[StorageAdapter] Failed to hydrate key ${key}:`, e);
            }
        }));

        console.log(`[StorageAdapter] Hydration complete. Validated ${count}/${hotDataKeys.length} keys.`);
    }

    /**
     * Debounced copy of sandbox offline DB → public Lytsoft (Clear-data durable).
     * @param {string} [reason]
     */
    scheduleLytsoftBackup(reason = 'write') {
        if (this.platform !== 'mobile' || Capacitor.getPlatform() !== 'android') return;
        if (this._lytsoftBackupTimer) clearTimeout(this._lytsoftBackupTimer);
        this._lytsoftBackupTimer = setTimeout(() => {
            this.backupToLytsoft(reason).catch(() => {});
        }, reason === 'pause' ? 0 : 1500);
    }

    /**
     * Checkpoint WAL if possible, then backup sandbox → Lytsoft.
     * @param {string} [reason]
     * @returns {Promise<{ backedUp: boolean, reason?: string }>}
     */
    async backupToLytsoft(reason = 'manual') {
        if (this.platform !== 'mobile' || Capacitor.getPlatform() !== 'android') {
            return { backedUp: false, reason: 'not_android' };
        }
        try {
            if (this.dbConnection) {
                try {
                    await this.dbConnection.execute('PRAGMA wal_checkpoint(FULL);', false);
                } catch (_) {
                    /* best effort */
                }
            }
            const { backupAndroidOfflineDb } = await import('./lytsoftDataDir');
            const res = await backupAndroidOfflineDb();
            if (res && res.backedUp) {
                console.log('[StorageAdapter] Lytsoft backup ok (' + reason + ')');
            }
            return res || { backedUp: false };
        } catch (e) {
            console.warn('[StorageAdapter] Lytsoft backup failed:', e && e.message);
            return { backedUp: false, reason: (e && e.message) || 'error' };
        }
    }
}

// Singleton instance
let storageInstance = null;

/**
 * Get storage adapter instance
 * @returns {Promise<StorageAdapter>} Storage adapter
 */
export async function getStorage() {
    if (!storageInstance) {
        storageInstance = new StorageAdapter();
        await storageInstance.init();
    }
    return storageInstance;
}

/**
 * Helper function for simple key-value operations
 * @param {string} table Table/store name
 * @param {string} key Key
 * @param {any} value Value (optional, if omitted will GET)
 * @returns {Promise<any>}
 */
export async function kvStore(table, key, value = undefined) {
    const storage = await getStorage();

    if (value === undefined) {
        // GET operation
        const row = await storage.get(
            `SELECT value FROM ${table} WHERE key = ? `,
            [key]
        );
        return row ? JSON.parse(row.value) : undefined;
    } else {
        // SET operation
        await storage.run(
            `INSERT INTO ${table} (key, value, updated_at)
             VALUES(?, ?, CURRENT_TIMESTAMP)
                 ON CONFLICT(key) DO UPDATE SET value = ?, updated_at = CURRENT_TIMESTAMP`,
            [key, JSON.stringify(value), JSON.stringify(value)]
        );
    }
}

/**
 * Get value by key (Storage.js compatibility)
 * @param {string} key Key to retrieve
 * @param {string} storeName Store name (main_store, admin_users, sessions)
 * @returns {Promise<any>} Value or undefined
 */
export async function getValue(key, storeName = 'main_store') {
    const storage = await getStorage();
    return storage.getValue(key, storeName);
}

/**
 * Set value by key (Storage.js compatibility)
 * @param {string} key Key to set
 * @param {any} value Value to store
 * @param {string} storeName Store name (main_store, admin_users, sessions)
 * @returns {Promise<void>}
 */
export async function setValue(key, value, storeName = 'main_store') {
    const storage = await getStorage();
    return storage.setValue(key, value, storeName);
}

/**
 * Backup sandbox offline DB to public Lytsoft (Android).
 * @returns {Promise<{ backedUp: boolean, reason?: string }>}
 */
export async function backupOfflineDbToLytsoft() {
    const storage = await getStorage();
    if (storage && typeof storage.backupToLytsoft === 'function') {
        return storage.backupToLytsoft('export');
    }
    return { backedUp: false, reason: 'no_storage' };
}

export default {
    getStorage,
    kvStore,
    getValue,
    setValue
};
